/**
 * The list a record page was opened from, carried in the browser's history
 * state rather than in a store. It survives a reload and the back button, and
 * it is absent for an address someone pasted, which is exactly when the spec
 * says "1 of 20" and the arrows must not show.
 */

export interface RecordListContext {
    /** The ids of the rows the list had loaded, in order. */
    ids: number[];
    /** The breadcrumb's list part, for example "Internal players" or "All players". */
    label: string;
    /** The list's address, query included, for the breadcrumb link. */
    path: string;
}

const STATE_KEY = 'recordList';

export function recordListState(context: RecordListContext): { recordList: { ids: number[]; label: string; path: string } } {
    return { recordList: { ids: context.ids, label: context.label, path: context.path } };
}

/** Reads the context back from a history state, or null when there is none or it is broken. */
export function readRecordListContext(state: unknown): RecordListContext | null {
    if (typeof state !== 'object' || state === null) {
        return null;
    }
    const stored = (state as Record<string, unknown>)[STATE_KEY];
    if (typeof stored !== 'object' || stored === null) {
        return null;
    }
    const { ids, label, path } = stored as Record<string, unknown>;
    if (!Array.isArray(ids) || !ids.every((id) => typeof id === 'number') || typeof label !== 'string' || typeof path !== 'string') {
        return null;
    }
    return { ids: ids as number[], label, path };
}

export interface StepPosition {
    /** 1-based, for "1 of 20". */
    position: number;
    total: number;
    previousId: number | null;
    nextId: number | null;
}

/**
 * Where a record sits in its list, or null when it is not in the list it came
 * from. The arrows stop at the ends of the loaded rows; they never fetch the
 * next page.
 */
export function stepPosition(context: RecordListContext | null, recordId: number): StepPosition | null {
    if (!context) {
        return null;
    }
    const index = context.ids.indexOf(recordId);
    if (index === -1) {
        return null;
    }
    return {
        position: index + 1,
        total: context.ids.length,
        previousId: index > 0 ? context.ids[index - 1] : null,
        nextId: index < context.ids.length - 1 ? context.ids[index + 1] : null,
    };
}
